import { useState } from "react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  paidBuildingCreationBlock,
  paidBuildingSetupNeeded,
  quoteForBuildingPlan,
} from "./buildingPlanCreation";
import { useBillingOverview, useBillingPlans } from "./hooks";
import type { Translations } from "@/lib/i18n/types";
import type { BillingOverview, BillingPlan, BuildingPlanTier } from "./types";

export interface BuildingCreationPlanConsent {
  orgId: string;
  tier: BuildingPlanTier;
  canSubmit: boolean;
  reason?: string;
}

function matchingOverview(data: BillingOverview | undefined, orgId: string) {
  if (data?.context.org_id !== orgId) return undefined;
  return data;
}

interface QueryAvailability {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

function quoteAvailability(
  overview: QueryAvailability,
  plans: QueryAvailability,
  data?: BillingOverview,
  plan?: BillingPlan
) {
  const isLoading = Boolean(
    overview.isLoading || plans.isLoading || overview.isFetching || plans.isFetching
  );
  const unavailable = overview.isError || plans.isError || (!isLoading && (!data || !plan));
  return { isLoading, unavailable };
}

function creationReason(
  data: BillingOverview | undefined,
  tier: BuildingPlanTier,
  t: Translations
) {
  if (!data) return undefined;
  if (!data.context.is_org_owner) return t.billing.restrictions.ownerOnly;
  return paidBuildingCreationBlock(data.context, tier, t);
}

function consentReason(reason: string | undefined, unavailable: boolean, t: Translations) {
  if (reason !== undefined) return reason;
  if (unavailable) return t.billing.consent.quoteUnavailable;
  return undefined;
}

function canSubmitQuote(
  isLoading: boolean,
  unavailable: boolean,
  reason: string | undefined,
  data?: BillingOverview,
  plan?: BillingPlan
) {
  return !isLoading && !unavailable && !reason && Boolean(data && plan);
}

function creationQuote(
  isLoading: boolean,
  unavailable: boolean,
  data?: BillingOverview,
  plan?: BillingPlan
) {
  if (isLoading || unavailable || !data || !plan) return undefined;
  return quoteForBuildingPlan(data.add_building_quote, plan);
}

/** Mount only for owners, scoped to the organisation's form instance. */
export function useBuildingCreationPlan(orgId: string) {
  const { t } = useLanguage();
  const overview = useBillingOverview();
  const plans = useBillingPlans();
  const [tier, setTier] = useState<BuildingPlanTier>("free");
  const plan = plans.data?.find((entry) => entry.code === tier);
  const data = matchingOverview(overview.data, orgId);
  const { isLoading, unavailable } = quoteAvailability(overview, plans, data, plan);
  const reason = creationReason(data, tier, t);
  const consent: BuildingCreationPlanConsent = {
    orgId,
    tier,
    canSubmit: canSubmitQuote(isLoading, unavailable, reason, data, plan),
    reason: consentReason(reason, unavailable, t),
  };

  return {
    consent,
    tier,
    setTier,
    plans: plans.data,
    isLoading,
    unavailable,
    reason,
    needsBillingSetup: data ? paidBuildingSetupNeeded(data.context, tier) : false,
    quote: creationQuote(isLoading, unavailable, data, plan),
    currency: data?.context.currency,
    retry: () => {
      void overview.refetch();
      void plans.refetch();
    },
  };
}
